import { useRef, useState } from "react";
import { Calendar, Paperclip, Plus, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/chalkApi";
import { BottomSheet, ChalkActionButton } from "./primitives";

/**
 * Add Event sheet — quick coach flow to schedule a practice or game.
 *
 * Optional attachments: coaches can attach photos of a printed schedule,
 * practice-plan PDFs, or field-location maps. Files are uploaded to the
 * team's file store AFTER the event is created (so a failed event create
 * doesn't strand orphan uploads). The attached file rows are tied to the
 * event by sharing the team scope; future iterations can link them
 * one-to-many to the event row directly.
 */
export default function AddEventSheet({ isOpen, onDismiss, teamId, onCreated }) {
  const [kind, setKind] = useState("practice"); // practice | game
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(_todayIso());
  const [time, setTime] = useState("18:00");
  const [location, setLocation] = useState("");
  const [weatherZip, setWeatherZip] = useState("");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]); // File[] queued for upload
  const [saving, setSaving] = useState(false);
  const fileInput = useRef(null);

  function pickFiles(e) {
    const list = Array.from(e.target.files || []);
    // Cap each upload at 25MB (matches backend FILE_MAX_BYTES). Anything
    // bigger gets dropped with a friendly nudge — saves a round-trip.
    const ok = [];
    for (const f of list) {
      if (f.size > 25 * 1024 * 1024) {
        toast.error(`${f.name} is over 25MB. Compress it and try again.`);
        continue;
      }
      ok.push(f);
    }
    setFiles((prev) => [...prev, ...ok]);
    e.target.value = ""; // allow re-picking the same file
  }

  function removeFile(idx) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function submit() {
    if (!teamId) {
      toast.error("Create a team first.");
      return;
    }
    if (!title.trim()) {
      toast.error("Add a title for the event.");
      return;
    }
    setSaving(true);
    try {
      // Cheap client-side guard. Backend re-validates with a stricter
      // regex, so this is just to surface the error before the round-trip.
      const zipTrimmed = weatherZip.trim();
      if (zipTrimmed && !/^\d{5}(?:-\d{4})?$/.test(zipTrimmed)) {
        toast.error("ZIP should be 5 digits (or ZIP+4).");
        setSaving(false);
        return;
      }
      const start_local = `${date}T${time}:00`;
      const data = await api.createEvent(teamId, {
        title: title.trim(),
        event_type: kind,
        start_local,
        location: location.trim() || null,
        notes: notes.trim() || null,
        weather_zip: zipTrimmed || null,
      });
      // Upload attached files in parallel — failures here don't roll back
      // the event itself; we surface a soft warning so the coach can retry.
      if (files.length) {
        const results = await Promise.allSettled(files.map((f) => api.uploadFile(teamId, f)));
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed) toast.warning(`${failed} of ${files.length} files didn't upload. You can add them from The Chalkboard.`);
      }
      toast.success(kind === "game" ? "Game added." : "Practice added.");
      if (data.conflicts?.length) toast.warning(`Heads up: this overlaps ${data.conflicts.map((c) => c.title).join(", ")}.`);
      onCreated && onCreated(data.event);
      _reset();
      onDismiss && onDismiss();
    } catch (e) {
      toast.error(e.message || "Couldn't add event.");
    } finally {
      setSaving(false);
    }
  }

  function _reset() {
    setTitle("");
    setDate(_todayIso());
    setTime("18:00");
    setLocation("");
    setWeatherZip("");
    setNotes("");
    setKind("practice");
    setFiles([]);
  }

  return (
    <BottomSheet isOpen={isOpen} title="Add a play" onDismiss={onDismiss}>
      <div data-testid="add-event-sheet" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Kind toggle — practice / game */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <KindButton label="Practice" active={kind === "practice"} onClick={() => setKind("practice")} testId="add-kind-practice" />
          <KindButton label="Game" active={kind === "game"} onClick={() => setKind("game")} testId="add-kind-game" />
        </div>

        <LabeledInput
          label="What is it?"
          value={title}
          onChange={setTitle}
          placeholder={kind === "game" ? "Game vs Wildcats" : "Tuesday practice"}
          testId="add-event-title"
          autoFocus
        />

        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 8 }}>
          <LabeledInput label="Date" type="date" value={date} onChange={setDate} testId="add-event-date" />
          <LabeledInput label="Time" type="time" value={time} onChange={setTime} testId="add-event-time" />
        </div>

        <LabeledInput
          label="Location (optional)"
          value={location}
          onChange={setLocation}
          placeholder="Field 4"
          testId="add-event-location"
        />
        {/* Per-event weather ZIP override. The Family Dashboard + the
            Playbook weather pill resolve this to lat/lon and fetch the
            forecast for that exact zip. Useful for away games where the
            venue is in a different city than the team's home field. */}
        <LabeledInput
          label="Weather ZIP (optional)"
          value={weatherZip}
          onChange={setWeatherZip}
          placeholder="e.g. 94110"
          testId="add-event-weather-zip"
          type="text"
        />
        <LabeledInput
          label="Notes (optional)"
          value={notes}
          onChange={setNotes}
          placeholder="Bring cleats"
          testId="add-event-notes"
        />

        {/* Attachments — schedules, practice plans, field maps. JPG/PNG/PDF
            accepted by the backend whitelist; iOS lets parents tap-to-view. */}
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", marginBottom: 6 }}>
            Attachments (optional)
          </div>
          <input
            ref={fileInput}
            type="file"
            multiple
            accept="image/*,.pdf,.heic,.heif"
            data-testid="add-event-file-input"
            onChange={pickFiles}
            style={{ display: "none" }}
          />
          <button
            type="button"
            onClick={() => fileInput.current && fileInput.current.click()}
            data-testid="add-event-file-pick"
            style={{
              width: "100%", display: "inline-flex", alignItems: "center",
              justifyContent: "center", gap: 8,
              padding: "11px 12px", borderRadius: 10,
              background: "#fff", color: "var(--chalk-board)",
              border: "0.5px dashed var(--chalk-border)",
              cursor: "pointer", fontFamily: "inherit", fontSize: 14,
            }}
          >
            <Paperclip size={16} aria-hidden="true" /> Attach a photo or PDF
          </button>
          {files.length > 0 && (
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
              {files.map((f, i) => (
                <div
                  key={`${f.name}-${i}`}
                  data-testid={`add-event-file-${i}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "8px 10px", borderRadius: 8,
                    background: "rgba(46,125,82,0.06)",
                    fontSize: 13, color: "var(--chalk-board)",
                  }}
                >
                  <Paperclip size={13} aria-hidden="true" style={{ color: "var(--green-mid)" }} />
                  <div style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {f.name}
                  </div>
                  <span style={{ fontSize: 11, color: "var(--chalk-muted)" }}>{_fmtSize(f.size)}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    aria-label={`Remove ${f.name}`}
                    style={{ background: "transparent", border: "none", color: "var(--chalk-muted)", cursor: "pointer", display: "inline-flex" }}
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <ChalkActionButton
          testId="add-event-submit"
          variant="primary"
          label={saving ? "Adding…" : `Add ${kind}`}
          icon={<Plus size={16} aria-hidden="true" />}
          loading={saving}
          onClick={submit}
        />
      </div>
    </BottomSheet>
  );
}

function KindButton({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{
        padding: "11px 14px", borderRadius: 9,
        background: active ? "var(--chalk-board)" : "#fff",
        color: active ? "var(--chalk-white)" : "var(--chalk-board)",
        border: active ? "1px solid var(--chalk-board)" : "0.5px solid var(--chalk-border)",
        fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 13,
        letterSpacing: "0.04em",
        cursor: "pointer", transition: "background 120ms ease",
      }}
    >
      {label}
    </button>
  );
}

function LabeledInput({ label, value, onChange, placeholder, testId, type = "text", autoFocus }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{
        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em",
        color: "var(--chalk-muted)", marginBottom: 6,
      }}>{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        data-testid={testId}
        style={{
          width: "100%", border: "0.5px solid var(--chalk-border)",
          borderRadius: 10, padding: "11px 12px", fontSize: 16,
          fontFamily: "inherit", outline: "none",
          background: "#FBF8F2", color: "var(--chalk-board)",
        }}
      />
    </label>
  );
}

function _todayIso() {
  // YYYY-MM-DD in the user's local timezone — `<input type="date">` expects this.
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function _fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
